import { extractErrorMessage, removeInPlace, sleep } from '../utils'
import { Logger, logger } from './Logger'
import { MuseWSMessage } from '../pages/CollaboratePage/Types'

export class WSConnectionSendError {
    error: boolean = true
    code: string
    debugMessage: string

    constructor(code: string, debugMessage: string) {
        this.code = code
        this.debugMessage = debugMessage
    }
}

export type WSConnectionSendPolicyParams =
    | {
          isReliable: false
      }
    | {
          isReliable: true
          checkAck?: boolean
          tBetweenRetriesMs?: number
          totalTimeoutMs?: number
      }

export type WSConnectionSendPolicy =
    | {
          isReliable: false
      }
    | {
          isReliable: true
          checkAck: boolean
          tBetweenRetriesMs: number
          totalTimeoutMs: number
      }

export default class WSConnectionManager {
    private wsHost: string = ''
    private userUrn: string = ''
    private authToken: string | null = null
    private appSerialId: string = ''
    private sessionId: string = ''

    private ws: WebSocket | null = null
    private pingIntervalId: number | undefined
    private initializing = false

    // An application level ping ensures the connection stays open.
    private pingManager = new WSConnectionPingManager()
    private isUnresponsiveSocketTest = false

    private initializationTimeout: NodeJS.Timeout | undefined = undefined

    private log: Logger

    constructor() {
        this.log = logger.createChildLogger({ name: 'WSConnectionManager' })
    }

    public initialize(params: {
        wsHost: string
        userUrn: string
        authToken: string
        appSerialId: string
        sessionId: string
    }) {
        if (this.initializing) {
            this.log.verbose(`initialize`, `Already initializing, exiting.`)
            return
        }
        this.initializing = true

        const { wsHost, userUrn, authToken, appSerialId, sessionId } = params

        this.wsHost = wsHost
        this.userUrn = userUrn
        this.authToken = authToken
        this.appSerialId = appSerialId
        this.sessionId = sessionId

        this.isUnresponsiveSocketTest = false

        this.onopen = this.onopen.bind(this)
        this.onmessage = this.onmessage.bind(this)
        this.onclose = this.onclose.bind(this)
        this.onInitializationTimeout = this.onInitializationTimeout.bind(this)

        this.log.checkpoint(`initialize`, `ws_initialize`, { wsHost, userUrn, appSerialId, sessionId })

        // Setup signalling websocket server. Modifies this.signallingWS.
        this.setupSignallingWS()
    }

    private async teardown() {
        if (this.ws != null) {
            this.ws.onopen = null
            this.ws.onmessage = null
            this.ws.onerror = null
            this.ws.onclose = null

            try {
                this.log.verbose(`teardown`, `calling this.ws.close().`)

                // NOTE: must wait for the this.ws.close() to complete (via polling in our case)
                // because if we don't, creating a new WebSocket() results in the onopen()
                // callback not being called.
                this.ws.close()

                this.pingManager.stop()

                const timeoutMs = 3000
                const pollInterval = 1000

                // poll
                for (let i = 0; i < timeoutMs / pollInterval; ++i) {
                    this.log.verbose(`teardown`, `ws.readyState==${this.readyStateToString(this.ws.readyState)}.`)
                    if (this.ws.readyState === WebSocket.CLOSED) {
                        this.log.info(`teardown`, `ws closed successfully.`)
                        this.ws = null
                        return true
                    }
                    await sleep(pollInterval)
                }

                this.log.info(`teardown`, `ws.close() timed out.`, {
                    wsReadyState: this.readyStateToString(this.ws.readyState),
                })
                this.ws = null
                return false
            } catch (e) {
                this.log.assert(`teardown`, `this.ws.close() threw an exception.`)
                this.ws = null
                return false
            }
        } else {
            return true
        }
    }

    private async setupSignallingWS(): Promise<boolean> {
        clearTimeout(this.initializationTimeout)
        this.initializationTimeout = setTimeout(this.onInitializationTimeout, 5000)

        // Close existing WS
        this.log.verbose(`setupSignallingWS`, `closing existing connection if exists.`)
        await this.teardown()

        await sleep(100)

        // Create WS
        this.log.verbose(`setupSignallingWS`, `creating new WebSocket("${this.wsHost}").`)
        try {
            this.ws = new WebSocket(this.wsHost)

            // Identify ourselves to the signalling server as soon as the connection opens
            this.ws.onopen = this.onopen

            // Route messages
            this.ws.onmessage = this.onmessage

            // Handle errors
            this.ws.onerror = () => {}

            // Handle closing.
            // We assume that 'onclose' is called reliably.
            this.ws.onclose = this.onclose

            this.log.verbose(
                `setupSignallingWS`,
                `WebSocket object successfully created. Waiting for ` + `'onopen' event.`
            )
        } catch (e) {
            // WebSocket() constructor throws DOMException if url is malformed.
            // TODO: emitEvent() an unrecoverable error.
            this.log.assert('setupSignallingWS', 'failed to create WebSocket.', { e })
            return false
        }

        return true
    }

    async reconnect() {
        clearTimeout(this.initializationTimeout)

        const connectivityCheckResult = await this.checkConnectivity()

        this.log.checkpoint(`reconnect`, `ws_reconnect`, {
            connectivityCheckResult,
        })

        await this.teardown()

        this.reinitialize()
    }

    async testClose(closeForMs: number) {
        clearTimeout(this.initializationTimeout)

        this.log.info(`testClose`, `entered.`)

        await this.teardown()

        this.log.checkpoint('signalling_ws_lost')

        await sleep(closeForMs)

        this.log.checkpoint('signalling_ws_lost_retry')

        this.reinitialize()
    }

    async setTestUnresponsiveSocket() {
        this.isUnresponsiveSocketTest = true
        this.log.verbose('setTestUnresponsiveSocket', 'set isUnresponsiveSocketTest.')
    }

    private async onopen() {
        this.log.verbose(`onopen`, `onopen event emitted by ws.`)

        clearTimeout(this.initializationTimeout)
        this.initializing = false

        if (!this.ws) {
            // TODO: emit connection failure
            this.log.assert(`onopen`, `this.ws==null. this should be impossible.`)
            return
        }

        await sleep(100)

        if (this.ws.readyState !== WebSocket.OPEN) {
            // TODO: emit connection failure

            this.log.error(
                `onopen`,
                `ws.readyState==${this.readyStateToString(this.ws.readyState)} which is incorrect. ` +
                    `failed to establish connection.`
            )
            return
        }

        const data = {
            authToken: this.authToken,
            appSerialId: this.appSerialId,
            sessionId: this.sessionId,
            userUrn: this.userUrn,
            roomUrn: '',
        }

        this.log.checkpoint('signalling_ws_register', '', { data })

        this.ws.send(
            JSON.stringify({
                type: 'register',
                data: data,
            })
        )

        this.pingManager.onreset = () => {
            this.reconnect()
        }
        this.pingManager.onsend = (msg: string) => {
            if (this.ws && !this.isUnresponsiveSocketTest) {
                this.ws.send(msg)
            }
        }
        this.pingManager.sendInitialPing()

        this.emitEvent('onopen', {})
    }

    private async onmessage(ev: any) {
        if (this.isUnresponsiveSocketTest) {
            this.log.verbose(`onmessage`, `isTestBlockMessages is set so no-op.`)
            return
        }

        if (typeof ev !== 'object') {
            this.log.assert(`onmessage`, `typeof ev=="${typeof ev}". expected 'object'. no-op.`)
            return
        }
        if (ev.type !== 'message') {
            this.log.assert(`onmessage`, `expected ev.type=="message". instead got "${ev.type}". no-op.`)
            return
        }

        const msg = ev.data
        if (typeof msg !== 'string') {
            this.log.assert(`onmessage`, `typeof msg=="${typeof msg}". expected 'string'. no-op.`)
            return
        }

        this.log.isSilly() && this.log.silly(`onmessage`, `received message of length ${msg.length}.`)

        let json
        try {
            json = JSON.parse(msg)
            this.log.isSilly() &&
                this.log.silly(`onmessage`, `successfully parsed message. emitting event 'onmessage'.`, { json })
        } catch (e) {
            this.log.isVerbose() &&
                this.log.verbose(
                    `onmessage`,
                    `failed to parse message. message content: "${JSON.stringify(msg)} ` +
                        `message="${extractErrorMessage(e)}"`
                )
        }

        // special handling for 'pong'.
        if (json.type === 'pong') {
            this.pingManager.pongReceived()
            return
        }

        this.emitEvent('onmessage', json)
    }

    private async checkConnectivity() {
        return new Promise((resolve) => {
            let timeoutTimer = setTimeout(() => {
                resolve({ isConnected: null, timedOut: true })
            }, 10_000)

            fetch('https://api.musecore.net/api/ping')
                .then((response) => {
                    resolve({ isConnected: true, statusCode: response.status })
                })
                .catch((err) => {
                    resolve({
                        isConnected: false,
                        errorMessage: err?.message || 'Failed to connect to server',
                    })
                })
                .finally(() => {
                    clearTimeout(timeoutTimer)
                })
        })
    }

    private async onclose(ev: CloseEvent) {
        clearTimeout(this.initializationTimeout)
        clearInterval(this.pingIntervalId)
        this.initializing = false

        const connectivityCheckResult = await this.checkConnectivity()

        this.log.error(`onclose`, `code=${ev.code} reason="${ev.reason}"`, {
            code: ev.code,
            reason: ev.reason,
            navigatorOnLine: navigator.onLine,
            connectivityCheckResult,
        })

        await this.teardown()

        this.log.checkpoint('signalling_ws_lost')

        await sleep(3000)

        this.log.checkpoint('signalling_ws_lost_retry')

        this.reinitialize()
    }

    private reinitialize() {
        this.log.verbose(`reinitialize`, `calling this.initialize().`)
        this.initialize({
            wsHost: this.wsHost,
            userUrn: this.userUrn,
            authToken: this.authToken || '',
            appSerialId: this.appSerialId,
            sessionId: this.sessionId,
        })
    }

    private async onInitializationTimeout() {
        this.log.error(`onInitializationTimeout`, `onopen timed out.`)
        this.initializing = false

        await sleep(3000)

        this.log.verbose(`onInitializationTimeout`, `calling this.reinitialize().`)
        this.reinitialize()
    }

    private evMap: { [key: string]: Function[] } = {}

    private emitEvent(eventName: string, value: Object) {
        if (this.evMap[eventName]) {
            for (let f of this.evMap[eventName]) {
                f(value)
            }
        }
    }

    addEventListener(eventName: string, handler: Function) {
        if (this.evMap[eventName] == null) {
            this.evMap[eventName] = []
        }
        this.evMap[eventName].push(handler)
    }

    removeEventListener(eventName: string, handler: any) {
        removeInPlace(this.evMap[eventName] || [], (f) => f === handler)
    }

    /**
     * policies:
     *  - what to do if connection is obviously down (state is not OPEN, ws is null, exception on send() etc.)
     *     - let the caller know through the return value;
     *     - if 'reliable', then try to send it again after connection is re-established, with timeout.
     *       otherwise, do not retry.
     *  - if 'check_ack', then check that we receive an 'ack' from the server, with timeout. Function returns
     *    when we receive an ack, or returns error if it times out.
     *
     * socket may be CONNECTING, OPEN, or CLOSING/CLOSED at the time of the function call.
     *
     * If CONNECTING: for some calls, it may be optimal to poll for connection to become OPEN.
     * For others, it may be optimal to return an error immediately.
     */
    private lastMessageId = 0
    async sendMessage(
        obj: MuseWSMessage,
        _policy?: WSConnectionSendPolicyParams
    ): Promise<WSConnectionSendError | any> {
        return new Promise(async (resolve) => {
            if (!_policy) {
                _policy = {
                    isReliable: true,
                }
            }
            if (_policy.isReliable) {
                if (_policy.checkAck == null) _policy.checkAck = false
                if (_policy.tBetweenRetriesMs == null) _policy.tBetweenRetriesMs = 500
                if (_policy.totalTimeoutMs == null) _policy.totalTimeoutMs = 3000
            }
            const policy = _policy as WSConnectionSendPolicy

            if (!this.ws) {
                this.log.assert(`sendMessage`, `this.ws==null!`)
                if (!policy.isReliable) {
                    resolve(new WSConnectionSendError('not_connected', 'ws is null.'))
                    return
                }
                for (let t = 0; t < policy.totalTimeoutMs; t += policy.tBetweenRetriesMs) {
                    await sleep(policy.tBetweenRetriesMs)
                    // @ts-ignore
                    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                        break
                    }
                }
                if (!this.ws) {
                    resolve(new WSConnectionSendError('not_connected', 'timed out.'))
                    return
                }
            }

            if (this.ws.readyState === this.ws.OPEN) {
                // continue
            } else {
                if (!policy.isReliable) {
                    resolve(
                        new WSConnectionSendError(
                            'not_connected',
                            `ws.readyState=="${this.readyStateToString(this.ws.readyState)}".`
                        )
                    )
                    return
                }

                for (let t = 0; t < policy.totalTimeoutMs; t += policy.tBetweenRetriesMs) {
                    this.log.isSilly() &&
                        this.log.silly(
                            `sendMessage`,
                            `this.ws.readyState is not OPEN and policy.isReliable is true. ` +
                                `sleeping. (t=${t}, policy.timeoutMs=${policy.totalTimeoutMs}, policy.tBetweenRetriesMs=${policy.tBetweenRetriesMs})`
                        )
                    await sleep(policy.tBetweenRetriesMs)
                    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                        this.log.isSilly() &&
                            this.log.silly(
                                `sendMessage`,
                                `exited sleep. ws.readyState is OPEN. breaking out of the retry loop.`
                            )
                        break
                    }
                    this.log.silly(`sendMessage`, `exited sleep. ws.readyState is still not OPEN.`)
                }
                if (this.ws.readyState !== this.ws.OPEN) {
                    resolve(new WSConnectionSendError('not_connected', `timed out.`))
                    return
                }
            }

            try {
                if (policy.isReliable && policy.checkAck) {
                    if (!obj.uuid) {
                        obj.uuid = String(this.lastMessageId++)
                    }
                }

                this.log.isSilly() && this.log.silly(`sendMessage`, `sending "${obj.type}":`, { obj })
                if (!this.isUnresponsiveSocketTest) {
                    this.ws.send(JSON.stringify(obj))
                }

                // if expect reply:
                if (policy.isReliable && policy.checkAck) {
                    let timeoutTimer = setTimeout(() => {
                        this.removeEventListener('onmessage', handler)
                        resolve(
                            new WSConnectionSendError(
                                'response_timed_out',
                                `timed out while waiting for "server-reply" on message uuid="${obj.uuid}".`
                            )
                        )
                    }, policy.totalTimeoutMs)

                    const handler = (msg: MuseWSMessage) => {
                        if ((msg.type === 'server-reply' || msg.type === 'peer-reply') && msg.uuid === obj.uuid) {
                            clearTimeout(timeoutTimer)
                            this.removeEventListener('onmessage', handler)
                            resolve(msg.data)
                        }
                    }

                    this.addEventListener('onmessage', handler)
                } else {
                    resolve(null)
                }
            } catch (e) {
                resolve(
                    new WSConnectionSendError(
                        'not_connected',
                        `ws.send() threw an exception: "${extractErrorMessage(e)}"`
                    )
                )
            }
        })
    }

    private readonly webSocketStateLookup = {
        [WebSocket.CONNECTING]: 'CONNECTING',
        [WebSocket.OPEN]: 'OPEN',
        [WebSocket.CLOSING]: 'CLOSING',
        [WebSocket.CLOSED]: 'CLOSED',
    }
    private readyStateToString(readyState: number): string {
        // @ts-ignore
        return this.webSocketStateLookup[readyState] || '' + readyState
    }

    getSocket() {
        return this.ws
    }
}

class WSConnectionPingManager {
    // private ws: WebSocket | null = null
    private pingTimeout: NodeJS.Timeout | undefined
    private pongTimeout: NodeJS.Timeout | undefined
    private lastPingSentTime: number | null = null
    private readonly PING_INTERVAL_MS = 5000

    private log: Logger

    constructor() {
        this.log = logger.createChildLogger({ name: 'WSConnectionManager' })
    }

    onsend: ((msg: string) => void) | undefined
    onreset: (() => void) | undefined

    sendInitialPing() {
        this.sendPing()
    }

    stop() {
        clearTimeout(this.pingTimeout)
        clearTimeout(this.pongTimeout)
    }

    pongReceived() {
        clearTimeout(this.pongTimeout)
        this.scheduleNextPing()
    }

    private sendPing() {
        this.lastPingSentTime = Date.now()

        this.log.silly('sendPing', 'sending ping')

        if (this.onsend) {
            this.onsend(JSON.stringify({ type: 'ping' }))
        }

        this.pongTimeout = setTimeout(() => {
            this.log.error('timeout', 'ping timed out', { lastPingSentTime: this.lastPingSentTime })
            this.close()
        }, this.PING_INTERVAL_MS)
    }

    private scheduleNextPing() {
        if (!this.lastPingSentTime) {
            return
        }

        const elapsedMs = Date.now() - this.lastPingSentTime
        const remainingTime = Math.max(0, this.PING_INTERVAL_MS - elapsedMs)

        this.log.isSilly() && this.log.silly('scheduleNextPing', '', { elapsedMs, remainingTime })

        clearTimeout(this.pingTimeout)
        this.pingTimeout = setTimeout(() => {
            this.sendPing()
        }, remainingTime)
    }

    private close() {
        clearTimeout(this.pingTimeout)
        clearTimeout(this.pongTimeout)

        if (this.onreset) {
            this.onreset()
        }
    }
}
